// Fuel purchases tab — pure helpers (spec 2026-10-13-fuel-phase2-3 §5.5).
import { localDateTimeInTz, dateTimeInTz } from '@/features/staff/attendanceModel';
import { csvSafe } from './fuelEventsCsv';

/** The UTC ISO instant of a wall-clock `YYYY-MM-DD` + `HH:MM` in `tz` (same fixed-point walk as coldChain dayStartMs). */
export function wallClockToIso(day, time, tz) {
  const target = Date.parse(`${day}T${time}:00Z`);
  if (!Number.isFinite(target)) return null;
  let guess = target;
  for (let i = 0; i < 3; i += 1) {
    const wall = Date.parse(`${localDateTimeInTz(new Date(guess).toISOString(), tz)}:00Z`);
    if (wall === target) break;
    guess -= wall - target;
  }
  return new Date(guess).toISOString();
}

export function statusTone(matchStatus) {
  if (matchStatus === 'matched') return 'green';
  if (matchStatus === 'no_fill') return 'amber';
  return 'grey';
}

export function statusLabel(matchStatus, t) {
  if (matchStatus === 'matched') return t('fuelPage.purchases.status.matched', 'Matched');
  if (matchStatus === 'no_fill') return t('fuelPage.purchases.status.noFill', 'No refuel detected');
  return t('fuelPage.purchases.status.pending', 'Waiting for a refuel');
}

export function formatVariance(v) {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return `${v < 0 ? '−' : '+'}${Math.abs(v).toFixed(1)} L`;
}

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const textOrNull = (v) => {
  const s = String(v ?? '').trim();
  return s === '' ? null : s;
};

export function emptyPurchaseForm(tz, now = new Date()) {
  return {
    deviceId: '', purchasedAt: localDateTimeInTz(now.toISOString(), tz), litres: '', pricePerLitre: '', total: '', currency: 'IQD',
    station: '', odometerKm: '', externalRef: '', note: '', receiptFileId: null, receiptUrl: null,
  };
}

/** Form (wall-clock in the org timezone) → request body. `total` is computed by the server. */
export function formToBody(form, tz) {
  const [day, time] = String(form.purchasedAt ?? '').split('T');
  return {
    deviceId: form.deviceId === '' ? null : Number(form.deviceId),
    purchasedAt: day && time ? wallClockToIso(day, time.slice(0, 5), tz) : null,
    litres: numOrNull(form.litres),
    pricePerLitre: numOrNull(form.pricePerLitre),
    currency: textOrNull(form.currency),
    station: textOrNull(form.station),
    odometerKm: numOrNull(form.odometerKm),
    externalRef: textOrNull(form.externalRef),
    note: textOrNull(form.note),
    receiptFileId: form.receiptFileId ?? null,
  };
}

const str = (v) => (v === null || v === undefined ? '' : String(v));
export function bodyToForm(row, tz) {
  return {
    deviceId: row.deviceId ?? '',
    purchasedAt: localDateTimeInTz(row.purchasedAt, tz),
    litres: str(row.litres),
    pricePerLitre: str(row.pricePerLitre),
    total: str(row.total),
    currency: row.currency ?? 'IQD',
    station: str(row.station),
    odometerKm: str(row.odometerKm),
    externalRef: str(row.externalRef),
    note: str(row.note),
    receiptFileId: row.receiptFileId ?? null,
    receiptUrl: row.receiptUrl ?? null,
  };
}

export const CSV_KEYS = ['vehicle', 'time', 'litres', 'price', 'total', 'currency', 'station', 'status', 'variance', 'reference'];
const HEADERS = {
  vehicle: 'Vehicle', time: 'Time', litres: 'Litres', price: 'Price per litre', total: 'Total', currency: 'Currency',
  station: 'Station', status: 'Status', variance: 'Variance (L)', reference: 'Reference',
};
export function purchaseHeader(key, t) { return t(`fuelPage.purchases.csv.${key}`, HEADERS[key]); }

const num = (v, d = 1) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '');
/** One export cell, plain text (no CSV guard). */
export function purchaseCell(key, r, t, tz) {
  switch (key) {
    case 'vehicle': return str(r.deviceName);
    case 'time': return r.purchasedAt ? dateTimeInTz(r.purchasedAt, tz) : '';
    case 'litres': return num(r.litres);
    case 'price': return typeof r.pricePerLitre === 'number' ? String(r.pricePerLitre) : '';
    case 'total': return typeof r.total === 'number' ? String(r.total) : '';
    case 'currency': return typeof r.total === 'number' || typeof r.pricePerLitre === 'number' ? str(r.currency) : '';
    case 'station': return str(r.station);
    case 'status': return statusLabel(r.matchStatus, t);
    case 'variance': return num(r.varianceL);
    case 'reference': return str(r.externalRef);
    default: return '';
  }
}

const TEXT_KEYS = new Set(['vehicle', 'station', 'reference']);
export function purchasesCsvGrid(rows, t, tz) {
  return {
    headers: CSV_KEYS.map((k) => purchaseHeader(k, t)),
    rows: rows.map((r) => CSV_KEYS.map((k) => (TEXT_KEYS.has(k) ? csvSafe(purchaseCell(k, r, t, tz)) : purchaseCell(k, r, t, tz)))),
  };
}
